import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useLanguage, api } from '../context/AppContext';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, UtensilsCrossed, Dumbbell, Droplets, Loader2, Activity, ShieldAlert, Footprints, Mic, Moon } from 'lucide-react';
import { toast } from 'sonner';

/**
 * GlobalFAB — Iter. 411.h (GLOW rework)
 *
 * Modernized Floating Action Button and radial menu to match the GLOW
 * aesthetic used across YUMIX (see `QuickMealLog` "Comme hier" card and
 * `RankUpModal`). Each action tile is now a dark glass-morphism pill with
 * a color-specific border + neon glow instead of the old flat pastel bar.
 *
 * Visual contract (do NOT downgrade without asking Isabelle):
 *   - Main "+" button uses layered orange radial glow + orbital pulse.
 *   - Each action pill: linear-gradient(rgba(0,0,0,0.55) → color/12),
 *     1px color border @ 0.4α, box-shadow 0 0 16px color/35.
 *   - When menu is open, "+" rotates 45° AND its color pivots to red so
 *     users immediately see it now acts as a "close" affordance.
 */
// LocalStorage key used to make the hydration-goal celebration fire at
// most once per day. Value = ISO date (YYYY-MM-DD) of last fire.
const HYDRATION_CELEBRATION_KEY = 'yumix_hydration_celebration_date';

const _todayIso = () => new Date().toISOString().slice(0, 10);

// Particle burst — pre-computed radial vectors so React doesn't re-roll
// randomness on each render. 14 particles is enough to feel lush without
// killing low-end Android GPUs.
const _PARTICLES = Array.from({ length: 14 }, (_, i) => {
  const angle = (Math.PI * 2 * i) / 14 + (i % 2 === 0 ? 0.12 : -0.12);
  const distance = 70 + (i % 3) * 18;
  return {
    id: i,
    x: Math.cos(angle) * distance,
    y: Math.sin(angle) * distance,
    delay: (i % 5) * 0.03,
    hue: i % 3 === 0 ? '253,224,71' : i % 3 === 1 ? '251,146,60' : '249,115,22',
  };
});

export function GlobalFAB() {
  const { language } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [loggingWater, setLoggingWater] = useState(false);
  const [hydration, setHydration] = useState({ total_ml: 0, target_ml: 3000 });
  const [celebrating, setCelebrating] = useState(false);

  const fetchHydration = useCallback(async () => {
    try {
      const res = await api.get('/hydration/today');
      setHydration({ total_ml: res.data.total_ml || 0, target_ml: res.data.target_ml || 3000 });
    } catch {}
  }, []);

  useEffect(() => { fetchHydration(); }, [fetchHydration]);

  // Close menu on route change
  useEffect(() => { setOpen(false); }, [location.pathname]);

  // Hide FAB on messaging page to avoid overlapping the send button
  if (location.pathname === '/messaging') return null;

  const addWater = async () => {
    // Optimistic update: bump the local total immediately so rapid taps feel snappy.
    // The backend is the source of truth; we reconcile with today_total_ml on the response.
    setHydration(prev => ({ ...prev, total_ml: (prev.total_ml || 0) + 250 }));
    setLoggingWater(true);
    try {
      const res = await api.post('/hydration/log', { amount_ml: 250 });
      const newTotal = res.data.today_total_ml;
      if (typeof newTotal === 'number') {
        setHydration(prev => ({ ...prev, total_ml: newTotal }));
      }
      const glasses = Math.round((newTotal || 0) / 250);
      toast.success(
        language === 'fr'
          ? `+1 verre (${glasses} total)`
          : `+1 glass (${glasses} total)`
      );
      if (res.data.percentage >= 100) {
        toast.success(language === 'fr' ? 'Objectif hydratation atteint !' : 'Hydration goal reached!');
        // Fire the particle celebration at most once per day. We check
        // localStorage BEFORE writing so a fast double-tap right at the
        // threshold cannot double-fire the animation.
        try {
          const lastFire = localStorage.getItem(HYDRATION_CELEBRATION_KEY);
          if (lastFire !== _todayIso()) {
            localStorage.setItem(HYDRATION_CELEBRATION_KEY, _todayIso());
            setCelebrating(true);
            // Auto-close after the framer-motion exit animation completes.
            setTimeout(() => setCelebrating(false), 1600);
          }
        } catch { /* localStorage unavailable — silent */ }
      }
    } catch {
      // Roll back optimistic update on failure
      setHydration(prev => ({ ...prev, total_ml: Math.max(0, (prev.total_ml || 0) - 250) }));
      toast.error(language === 'fr' ? "Erreur d'enregistrement" : 'Error logging water');
    } finally {
      setLoggingWater(false);
      // NOTE: menu stays open on +water taps so the user can log several glasses in a row.
    }
  };

  const glasses = Math.round(hydration.total_ml / 250);
  const pct = hydration.target_ml > 0 ? Math.min(100, Math.round((hydration.total_ml / hydration.target_ml) * 100)) : 0;

  // Each action: `rgba` is the base color used for border/glow so the
  // gradient reads cohesively with the rest of the GLOW system.
  const actions = [
    {
      id: 'voice',
      label: language === 'fr' ? 'Dicter a l\'IA' : 'Dictate to AI',
      icon: Mic,
      rgba: '251,146,60',   // orange-400
      textColor: 'text-orange-100',
      iconColor: 'text-orange-200',
      onClick: () => { setOpen(false); navigate('/coach-ai?auto=voice'); },
    },
    {
      id: 'meal',
      label: language === 'fr' ? 'Loguer un repas' : 'Log a meal',
      icon: UtensilsCrossed,
      rgba: '16,185,129',   // emerald-500
      textColor: 'text-emerald-100',
      iconColor: 'text-emerald-200',
      onClick: () => { setOpen(false); navigate('/dashboard', { state: { openMealAdd: true } }); },
    },
    {
      id: 'workout',
      label: language === 'fr' ? 'Entraînement' : 'Workout',
      icon: Dumbbell,
      rgba: '245,158,11',   // amber-500
      textColor: 'text-amber-100',
      iconColor: 'text-amber-200',
      onClick: () => { setOpen(false); navigate('/workouts?tab=plan'); },
    },
    {
      id: 'activity',
      label: language === 'fr' ? 'Activite physique' : 'Physical activity',
      icon: Activity,
      rgba: '239,68,68',    // red-500
      textColor: 'text-red-100',
      iconColor: 'text-red-200',
      onClick: () => { setOpen(false); navigate('/activities'); },
    },
    {
      id: 'steps',
      label: language === 'fr' ? 'Nombre de pas' : 'Step count',
      icon: Footprints,
      rgba: '99,102,241',   // indigo-500
      textColor: 'text-indigo-100',
      iconColor: 'text-indigo-200',
      onClick: () => { setOpen(false); navigate('/walking'); },
    },
    {
      id: 'water',
      label: language === 'fr' ? `+1 verre (${glasses}/${Math.round(hydration.target_ml / 250)})` : `+1 glass (${glasses}/${Math.round(hydration.target_ml / 250)})`,
      icon: loggingWater ? Loader2 : Droplets,
      rgba: '59,130,246',   // blue-500
      textColor: 'text-sky-100',
      iconColor: 'text-sky-200',
      onClick: addWater,
      // NOTE: no `disabled: loggingWater` — we intentionally accept rapid-fire taps
      // so users can log multiple glasses without waiting for the network round-trip.
      loading: loggingWater,
      badge: pct >= 100 ? '100%' : `${pct}%`,
    },
    {
      id: 'sleep',
      label: language === 'fr' ? 'Sommeil' : 'Sleep',
      icon: Moon,
      rgba: '168,85,247',   // purple-500
      textColor: 'text-purple-100',
      iconColor: 'text-purple-200',
      onClick: () => { setOpen(false); navigate('/sleep'); },
    },
    {
      id: 'sos',
      label: language === 'fr' ? 'Urgence Faim' : 'Craving SOS',
      icon: ShieldAlert,
      rgba: '244,63,94',    // rose-500
      textColor: 'text-rose-100',
      iconColor: 'text-rose-200',
      onClick: () => { setOpen(false); navigate('/sos-craving'); },
    },
  ];

  return (
    <>
      {/* Backdrop — deepen when open to lift the GLOW menu off the page */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[998]"
            style={{ background: 'radial-gradient(circle at bottom right, rgba(251,146,60,0.12) 0%, rgba(0,0,0,0.6) 55%)', backdropFilter: 'blur(2px)' }}
            onClick={() => setOpen(false)}
          />
        )}
      </AnimatePresence>

      {/* FAB Container */}
      <div className="fixed right-4 z-[999] md:top-4 md:right-4 md:bottom-auto md:left-auto" style={{ bottom: 'calc(88px + env(safe-area-inset-bottom, 0px))' }} data-testid="global-fab-container">
        {/* Action items — expand UPWARD to avoid bottom nav collision */}
        <AnimatePresence>
          {open && (
            <motion.div
              initial={{ opacity: 0, y: 20, scale: 0.85 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 20, scale: 0.85 }}
              transition={{ type: 'spring', damping: 20, stiffness: 300 }}
              className="absolute bottom-[56px] right-0 md:top-[52px] md:bottom-auto flex flex-col gap-2 items-end"
            >
              {actions.map((action, i) => (
                <motion.button
                  key={action.id}
                  initial={{ opacity: 0, x: 30 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 30 }}
                  transition={{ delay: i * 0.05, type: 'spring', stiffness: 320, damping: 24 }}
                  onClick={action.onClick}
                  disabled={action.disabled}
                  className={`group relative flex items-center gap-2 pl-4 pr-2.5 py-2 rounded-full text-sm w-60 overflow-hidden active:scale-95 transition-transform`}
                  style={{
                    background: `linear-gradient(135deg, rgba(0,0,0,0.72) 0%, rgba(15,15,15,0.68) 45%, rgba(${action.rgba},0.18) 100%)`,
                    border: `1px solid rgba(${action.rgba},0.45)`,
                    boxShadow: `0 0 16px rgba(${action.rgba},0.35), inset 0 1px 0 rgba(255,255,255,0.06)`,
                    backdropFilter: 'blur(8px)',
                  }}
                  data-testid={`global-fab-${action.id}`}
                >
                  {/* Ambient corner flare */}
                  <span
                    aria-hidden="true"
                    className="absolute -right-6 -top-6 w-16 h-16 rounded-full pointer-events-none opacity-70 group-hover:opacity-100 transition-opacity"
                    style={{ background: `radial-gradient(circle, rgba(${action.rgba},0.42) 0%, transparent 70%)` }}
                  />
                  <span
                    className={`flex-1 text-center text-[11px] font-black uppercase tracking-wider whitespace-nowrap flex items-center justify-center gap-1.5 ${action.textColor}`}
                    style={{ textShadow: `0 0 8px rgba(${action.rgba},0.55)` }}
                  >
                    {action.label}
                    {action.badge && (
                      <span
                        className="text-[9px] font-mono font-black px-1.5 py-0.5 rounded-full"
                        style={{
                          background: `rgba(${action.rgba},0.22)`,
                          border: `1px solid rgba(${action.rgba},0.45)`,
                          color: `rgba(${action.rgba},1)`,
                        }}
                      >
                        {action.badge}
                      </span>
                    )}
                  </span>
                  <span
                    className="shrink-0 w-8 h-8 rounded-full flex items-center justify-center"
                    style={{
                      background: `linear-gradient(140deg, rgba(${action.rgba},0.45), rgba(${action.rgba},0.18))`,
                      boxShadow: `0 0 12px rgba(${action.rgba},0.55), inset 0 0 0 1px rgba(${action.rgba},0.5)`,
                    }}
                  >
                    <action.icon
                      className={`w-4 h-4 ${action.iconColor} ${action.disabled || action.loading ? 'animate-spin' : ''}`}
                      style={{ filter: `drop-shadow(0 0 6px rgba(${action.rgba},0.9))` }}
                    />
                  </span>
                </motion.button>
              ))}
            </motion.div>
          )}
        </AnimatePresence>

        {/* Main FAB button — GLOW aesthetic (orange when idle, red when open) */}
        <div className="relative">
          {/* Hydration goal celebration — particle burst, fires <=1×/day */}
          <AnimatePresence>
            {celebrating && (
              <div
                key="celebration"
                className="absolute inset-0 pointer-events-none"
                data-testid="hydration-celebration"
              >
                {/* Expanding ring */}
                <motion.span
                  aria-hidden="true"
                  className="absolute inset-0 rounded-full"
                  initial={{ scale: 0.6, opacity: 0.9 }}
                  animate={{ scale: 3.2, opacity: 0 }}
                  transition={{ duration: 1.1, ease: 'easeOut' }}
                  style={{
                    border: '2px solid rgba(251,146,60,0.85)',
                    boxShadow: '0 0 40px rgba(251,146,60,0.55)',
                  }}
                />
                {/* Radial particles */}
                {_PARTICLES.map((p) => (
                  <motion.span
                    key={p.id}
                    aria-hidden="true"
                    className="absolute top-1/2 left-1/2 w-2 h-2 rounded-full"
                    initial={{ x: -4, y: -4, opacity: 1, scale: 1 }}
                    animate={{ x: p.x - 4, y: p.y - 4, opacity: 0, scale: 0.4 }}
                    transition={{ duration: 1.1, delay: p.delay, ease: [0.16, 1, 0.3, 1] }}
                    style={{
                      background: `rgba(${p.hue},1)`,
                      boxShadow: `0 0 12px rgba(${p.hue},0.9), 0 0 22px rgba(${p.hue},0.55)`,
                    }}
                  />
                ))}
              </div>
            )}
          </AnimatePresence>

          {/* Orbital pulse ring (idle only) — subtle "come play" beacon */}
          <AnimatePresence>
            {!open && (
              <motion.span
                key="pulse"
                aria-hidden="true"
                className="absolute inset-0 rounded-full pointer-events-none"
                style={{ boxShadow: '0 0 0 0 rgba(251,146,60,0.55)' }}
                initial={{ scale: 1, opacity: 0.6 }}
                animate={{ scale: 1.6, opacity: 0 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 1.8, repeat: Infinity, ease: 'easeOut' }}
              />
            )}
          </AnimatePresence>

          <motion.button
            whileTap={{ scale: 0.88 }}
            onClick={() => setOpen(prev => !prev)}
            className="relative w-12 h-12 rounded-full flex items-center justify-center transition-all duration-300"
            style={{
              background: open
                ? 'linear-gradient(140deg, #ef4444 0%, #b91c1c 55%, #7f1d1d 100%)'
                : 'linear-gradient(140deg, #fb923c 0%, #f97316 45%, #c2410c 100%)',
              border: open
                ? '1.5px solid rgba(239,68,68,0.7)'
                : '1.5px solid rgba(251,146,60,0.7)',
              boxShadow: open
                ? '0 0 24px rgba(239,68,68,0.65), 0 0 8px rgba(239,68,68,0.4), inset 0 1px 0 rgba(255,255,255,0.25)'
                : '0 0 26px rgba(251,146,60,0.6), 0 0 10px rgba(251,146,60,0.45), inset 0 1px 0 rgba(255,255,255,0.25)',
            }}
            data-testid="global-fab-btn"
          >
            {/* Inner highlight ring */}
            <span
              aria-hidden="true"
              className="absolute inset-1 rounded-full pointer-events-none"
              style={{
                background:
                  'radial-gradient(circle at 30% 25%, rgba(255,255,255,0.35) 0%, rgba(255,255,255,0.05) 40%, transparent 70%)',
              }}
            />
            <Plus
              className={`w-6 h-6 text-white relative transition-transform duration-300 ${open ? 'rotate-45' : ''}`}
              strokeWidth={3}
              style={{ filter: 'drop-shadow(0 0 6px rgba(255,255,255,0.85))' }}
            />
          </motion.button>
        </div>
      </div>
    </>
  );
}
